import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiUsers, 
  FiThumbsUp, 
  FiThumbsDown, 
  FiShare2,
  FiUser,
  FiPhone,
  FiClock,
  FiX
} from 'react-icons/fi';
import { database, ref as dbRef, get, set } from '../firebase';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

const ShowlikeUnlikeusers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('likes');
  const [userDetails, setUserDetails] = useState({});
  // Add search query state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Engagement data
  const [likes, setLikes] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [shares, setShares] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [pointsInput, setPointsInput] = useState({});
  const [pointsLoading, setPointsLoading] = useState({});
  const [givenPoints, setGivenPoints] = useState({});

  // Fetch given points for each user in the current tab
  useEffect(() => {
    if (!state) return;
    const typeKey = activeTab === "dislikes" ? "unlike" : activeTab === "subscribers" ? "subscribe" : activeTab;
    const sourceMobile = state.userMobile;
    let currentData = [];
    if (activeTab === "likes") currentData = likes;
    else if (activeTab === "dislikes") currentData = dislikes;
    else if (activeTab === "shares") currentData = shares;
    else if (activeTab === "subscribers") currentData = subscribers;
    else currentData = [];
    if (!currentData.length) {
      setGivenPoints({});
      return;
    }
    const fetchAllGivenPoints = async () => {
      const givenPointsData = {};
      await Promise.all(
        currentData.map(async ([mobile]) => {
          const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
          const getExtraPointsRef = dbRef(
            database,
            `videoplayer/Users/${sanitizedMobile}/getextrapoints/${typeKey}/${sourceMobile}`
          );
          const extraPointsSnapshot = await get(getExtraPointsRef);
          givenPointsData[mobile] = extraPointsSnapshot.exists() ? parseInt(extraPointsSnapshot.val(), 10) || 0 : 0;
        })
      );
      setGivenPoints(givenPointsData);
    };
    fetchAllGivenPoints();
    // eslint-disable-next-line
  }, [activeTab, likes, dislikes, shares, subscribers, state]);

  useEffect(() => {
    if (!state) {
      setError('No media information provided');
      setLoading(false);
      return;
    }

    fetchEngagementUsers();
  }, [state]);

  const fetchEngagementUsers = async () => {
    try {
      setLoading(true);
      const { type, mediaKey, userMobile, mediaTitle } = state;
      const sanitizedMobile = userMobile.replace(/[^0-9]/g, '_');

      // Fetch likes
      const likesRef = dbRef(database, `videoplayer/${type === 'photo' ? 'photoUpload' : 'videoUpload'}/${sanitizedMobile}/${mediaKey}/likes`);
      const likesSnapshot = await get(likesRef);
      const likesData = likesSnapshot.exists() ? Object.entries(likesSnapshot.val()) : [];
      setLikes(likesData);

      // Fetch shares (handle video/photo difference)
      const sharesRef = dbRef(
        database,
        `videoplayer/${type === 'photo' ? 'photoUpload' : 'videoUpload'}/${sanitizedMobile}/${mediaKey}/${type === 'photo' ? 'shares' : 'share'}`
      );
      const sharesSnapshot = await get(sharesRef);
      let sharesData = [];
      if (sharesSnapshot.exists()) {
        if (type === 'photo') {
          // {mobile: {timestamp, ...}}
          sharesData = Object.entries(sharesSnapshot.val());
        } else {
          // {index: {mobile, timestamp, ...}}
          sharesData = Object.entries(sharesSnapshot.val()).map(([idx, data]) => [data.mobile, data]);
        }
      }
      setShares(sharesData);

      // Fetch dislikes (only for videos)
      let dislikesData = [];
      if (type === 'video') {
        const dislikesRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${mediaKey}/dislikes`);
        const dislikesSnapshot = await get(dislikesRef);
        dislikesData = dislikesSnapshot.exists() ? Object.entries(dislikesSnapshot.val()) : [];
        setDislikes(dislikesData);
      }

      // Fetch subscribers (only for videos)
      let subscribersData = [];
      if (type === 'video') {
        const subscribersRef = dbRef(database, `videoplayer/subscriptions/${sanitizedMobile}`);
        const subscribersSnapshot = await get(subscribersRef);
        if (subscribersSnapshot.exists()) {
          subscribersData = Object.entries(subscribersSnapshot.val()).map(([subscriberMobile, data]) => [subscriberMobile, data]);
        }
        setSubscribers(subscribersData);
      }

      // Fetch user details for all mobile numbers
      const allMobiles = new Set();
      likesData.forEach(([mobile]) => allMobiles.add(mobile));
      sharesData.forEach(([mobile]) => allMobiles.add(mobile));
      if (type === 'video') {
        dislikesData.forEach(([mobile]) => allMobiles.add(mobile));
        subscribersData.forEach(([mobile]) => allMobiles.add(mobile));
      }

      const userDetailsData = {};
      await Promise.all(
        Array.from(allMobiles).map(async (mobile) => {
          try {
            const sanitizedUserMobile = mobile.replace(/[^0-9]/g, '_');
            const userRef = dbRef(database, `videoplayer/Users/${sanitizedUserMobile}`);
            const userSnapshot = await get(userRef);
            if (userSnapshot.exists()) {
              const userData = userSnapshot.val();
              userDetailsData[mobile] = {
                name: userData.name || 'N/A',
                surname: userData.surname || 'N/A',
                mobile: mobile
              };
            } else {
              userDetailsData[mobile] = {
                name: 'Unknown',
                surname: 'User',
                mobile: mobile
              };
            }
          } catch (err) {
            console.error(`Error fetching user details for ${mobile}:`, err);
            userDetailsData[mobile] = {
              name: 'Error',
              surname: 'Loading',
              mobile: mobile
            };
          }
        })
      );

      setUserDetails(userDetailsData);
    } catch (err) {
      console.error('Error fetching engagement users:', err);
      setError('Failed to load engagement data');
      toast.error('Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  };

  const handleGivePoints = async (mobile) => {
    const pointsToAdd = parseInt(pointsInput[mobile], 10);
    if (!pointsToAdd || pointsToAdd <= 0) {
      toast.error("Please enter a valid number of points");
      return;
    }
    setPointsLoading(prev => ({ ...prev, [mobile]: true }));
    try {
      const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
      const userRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}`);
      const userSnapshot = await get(userRef);
      let currentPoints = 0;
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        currentPoints = parseInt(userData.points, 10) || 0;
      }
      const newPoints = currentPoints + pointsToAdd;
      await set(userRef, { ...userSnapshot.val(), points: newPoints });

      // Update getextrapoints
      let typeKey = activeTab;
      if (typeKey === "dislikes") typeKey = "unlike";
      if (typeKey === "subscribers") typeKey = "subscribe";
      const sourceMobile = state.userMobile;
      const getExtraPointsRef = dbRef(
        database,
        `videoplayer/Users/${sanitizedMobile}/getextrapoints/${typeKey}/${sourceMobile}`
      );
      // Get current value
      const extraPointsSnapshot = await get(getExtraPointsRef);
      let prevExtra = 0;
      if (extraPointsSnapshot.exists()) {
        prevExtra = parseInt(extraPointsSnapshot.val(), 10) || 0;
      }
      await set(getExtraPointsRef, prevExtra + pointsToAdd);

      // Update totalpoints for this type
      let typeRef = dbRef(
        database,
        `videoplayer/Users/${sanitizedMobile}/getextrapoints/${typeKey}`
      );
      const typeSnapshot = await get(typeRef);
      let sum = 0;
      if (typeSnapshot.exists()) {
        const vals = typeSnapshot.val();
        Object.entries(vals).forEach(([key, value]) => {
          if (key !== 'totalpoints') {
            sum += parseInt(value, 10) || 0;
          }
        });
      }
      const totalPointsTypeRef = dbRef(
        database,
        `videoplayer/Users/${sanitizedMobile}/getextrapoints/${typeKey}/totalpoints`
      );
      await set(totalPointsTypeRef, sum);

      toast.success(`Added ${pointsToAdd} points to user ${mobile}!`);
      setPointsInput(prev => ({ ...prev, [mobile]: "" }));
      setGivenPoints(prev => ({
        ...prev,
        [mobile]: (prev[mobile] || 0) + pointsToAdd
      }));
    } catch (err) {
      toast.error("Failed to give points");
    }
    setPointsLoading(prev => ({ ...prev, [mobile]: false }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const renderUserTable = (data, type) => {
    if (data.length === 0) {
      return (
        <div className="text-center py-8">
          <FiUsers className="mx-auto text-4xl text-gray-400 mb-2" />
          <p className="text-gray-500">No {type} found</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Sr. No
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                User Details
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Mobile
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {type === 'likes' ? 'Liked At' : type === 'dislikes' ? 'Disliked At' : type === 'subscribers' ? 'Subscribed At' : 'Shared At'}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Given Points
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Give Points
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map(([mobile, data], index) => (
              <tr key={mobile} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 border-b">
                  {index + 1}
                </td>
                <td className="px-4 py-3 whitespace-nowrap border-b">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <FiUser className="h-6 w-6 text-indigo-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {userDetails[mobile]?.name} {userDetails[mobile]?.surname}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 border-b">
                  <div className="flex items-center">
                    <FiPhone className="mr-2 text-gray-400" />
                    {mobile}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 border-b">
                  <div className="flex items-center">
                    <FiClock className="mr-2 text-gray-400" />
                    {type === 'shares' ? formatTimestamp(data.sharedAt || data.timestamp) : 
                     type === 'subscribers' ? formatTimestamp(data.subscribedAt || data.timestamp) : 
                     formatTimestamp(data.timestamp)}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 border-b text-center">
                  {givenPoints[mobile] || 0}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 border-b">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      className="w-20 px-2 py-1 border rounded"
                      value={pointsInput[mobile] || ""}
                      onChange={e =>
                        setPointsInput(prev => ({
                          ...prev,
                          [mobile]: e.target.value.replace(/[^0-9]/g, "")
                        }))
                      }
                      placeholder="Points"
                      disabled={pointsLoading[mobile]}
                    />
                    <button
                      className={`px-3 py-1 rounded bg-amber-500 text-white font-semibold hover:bg-amber-600 transition ${
                        pointsLoading[mobile] ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                      disabled={pointsLoading[mobile] || !pointsInput[mobile]}
                      onClick={() => handleGivePoints(mobile)}
                    >
                      {pointsLoading[mobile] ? "Giving..." : "Give"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Filter data by search query
  const filterBySearch = (data) => {
    if (!searchQuery.trim()) return data;
    return data.filter(([mobile, data]) => {
      const name = userDetails[mobile]?.name?.toLowerCase() || '';
      const surname = userDetails[mobile]?.surname?.toLowerCase() || '';
      const fullName = (name + ' ' + surname).trim();
      return (
        name.includes(searchQuery.toLowerCase()) ||
        surname.includes(searchQuery.toLowerCase()) ||
        fullName.includes(searchQuery.toLowerCase())
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="h-16 w-16 border-4 border-indigo-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <FiX className="inline-block text-4xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <FiX className="inline-block text-4xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No Data Available</h2>
          <p className="text-gray-600 mb-4">No media information was provided</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { type, mediaTitle } = state;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-white hover:text-indigo-200 transition-colors"
            >
              <FiArrowLeft className="mr-2" />
              {/* <span className="font-medium">Back</span> */}
            </button>
            <h1 className="text-xl font-bold text-white text-center">
              {type === 'photo' ? 'Photo' : 'Video'} Engagement Users
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Media Info Card */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-md p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            {mediaTitle}
          </h2>
          <p className="text-gray-600">
            Type: {type === 'photo' ? 'Photo' : 'Video'}
          </p>
        </motion.div>

        {/* Search Bar */}
        <div className="mb-4 flex justify-end">
          <input
            type="text"
            className="px-4 py-2 border rounded-lg w-full max-w-xs"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-4 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab('likes')}
              className={`px-4 py-2 rounded-lg font-medium flex items-center whitespace-nowrap ${
                activeTab === 'likes'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-500 hover:text-gray-700 bg-white'
              }`}
            >
              <FiThumbsUp className="mr-2" />
              Likes ({likes.length})
            </button>
            {type === 'video' && (
              <button
                onClick={() => setActiveTab('dislikes')}
                className={`px-4 py-2 rounded-lg font-medium flex items-center whitespace-nowrap ${
                  activeTab === 'dislikes'
                    ? 'bg-red-600 text-white'
                    : 'text-gray-500 hover:text-gray-700 bg-white'
                }`}
              >
                <FiThumbsDown className="mr-2" />
                Dislikes ({dislikes.length})
              </button>
            )}
            <button
              onClick={() => setActiveTab('shares')}
              className={`px-4 py-2 rounded-lg font-medium flex items-center whitespace-nowrap ${
                activeTab === 'shares'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 hover:text-gray-700 bg-white'
              }`}
            >
              <FiShare2 className="mr-2" />
              Shares ({shares.length})
            </button>
            {type === 'video' && (
              <button
                onClick={() => setActiveTab('subscribers')}
                className={`px-4 py-2 rounded-lg font-medium flex items-center whitespace-nowrap ${
                  activeTab === 'subscribers'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-500 hover:text-gray-700 bg-white'
                }`}
              >
                <FiUsers className="mr-2" />
                Subscribers ({subscribers.length})
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-lg shadow-md overflow-hidden"
        >
          <div className="p-6">
            {activeTab === 'likes' && renderUserTable(filterBySearch(likes), 'likes')}
            {activeTab === 'dislikes' && type === 'video' && renderUserTable(filterBySearch(dislikes), 'dislikes')}
            {activeTab === 'shares' && renderUserTable(filterBySearch(shares), 'shares')}
            {activeTab === 'subscribers' && type === 'video' && renderUserTable(filterBySearch(subscribers), 'subscribers')}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ShowlikeUnlikeusers;
